import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";

import { fetchMedicalInfoByUserId, updateMedicalInfo, createMedicalInfo } from "../redux/thunks/medicalThunks";
import { fetchPersonalHistoryByUserId, addPersonalHistory } from "../redux/thunks/personalHistoryThunks";
import { addVitalSignal, fetchVitalSignals } from "../redux/thunks/vitalThunks";
import { fetchVaccinations, addVaccination, updateVaccination, removeVaccination } from "../redux/thunks/vaccinationThunks";
import { fetchSurgicalHistory, addSurgicalHistory, updateSurgicalHistory, removeSurgicalHistory } from "../redux/thunks/surgicalHistoryThunks";
import { fetchBmiHistoryByUserId, addBmiEntry } from "../redux/thunks/bmiHistoryThunks";
import { selectVaccinationData, selectVaccinationHasFetched, selectVaccinationLoading } from "../redux/selectors/vaccinationSelectors";

import normalizeNumber from "../functions/profile/normalizeNumber";
import calcBmi from "../functions/profile/calcBmi";
import buildBmiChart from "../functions/profile/buildBmiChart";

/* ==================== MODULE-LEVEL FETCH TRACKING ==================== */
const fetchingMedical = new Set();
const fetchingPersonalHistory = { ids: new Set(), lastUserId: null };
const fetchingVaccinations = { ids: new Set(), lastUserId: null };
const fetchingSurgicalHistory = { ids: new Set(), lastUserId: null };
const fetchingBmiHistory = { ids: new Set(), lastUserId: null };
const fetchingVitalSignals = { ids: new Set(), lastUserId: null };

/* ==================== UTILITIES ==================== */
const isPersistedId = (value) => /^\d+$/.test(String(value ?? "").trim());

const pickPersistedId = (...candidates) => {
  const found = candidates.find((candidate) => isPersistedId(candidate));
  return found === undefined ? null : String(found).trim();
};

const firstPresent = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") ?? "";

const calculateBMI = (weight, height) => {
  const w = parseFloat(normalizeNumber(weight));
  const h = parseFloat(normalizeNumber(height));
  if (!w || !h || h === 0) return null;
  return +(w / ((h / 100) ** 2)).toFixed(2);
};

const parseBP = (bpStr) => {
  if (!bpStr || typeof bpStr !== "string") return { sys: null, dia: null };
  const cleaned = bpStr.replace(/[^\d/]/g, "/");
  const [sysStr, diaStr] = cleaned.split("/").filter(Boolean);
  const sys = Number(sysStr);
  const dia = Number(diaStr);

  return {
    sys: Number.isFinite(sys) ? sys : null,
    dia: Number.isFinite(dia) ? dia : null,
  };
};

const normalizeHistoryType = (value) => String(value || "").trim().toUpperCase();

/* ✅ NOUVEAU : extrait un message d'erreur lisible depuis une erreur axios/thunk.
   Le backend NestJS renvoie généralement { message, error, statusCode } dans
   response.data. Sans ça, sectionErrors ne contenait que le NOM de la
   section ("allergies", "vaccinations"...) et jamais la RAISON — impossible
   de savoir pourquoi ça échouait sans rouvrir chaque thunk. */
const extractErrorDetail = (err) => {
  const backendMessage =
    err?.response?.data?.message ??
    err?.data?.message ??
    err?.message ??
    err?.error ??
    null;

  if (Array.isArray(backendMessage)) return backendMessage.join("; ");
  if (backendMessage) return String(backendMessage);

  try {
    return JSON.stringify(err);
  } catch {
    return "Unknown error";
  }
};

/* ==================== MAPPINGS BACKEND → UI ==================== */

const smokingBackendToUi = (value) => {
  if (!value) return { label: "Unknown", tone: "neutral" };
  const v = String(value).toLowerCase().trim();

  if (v === "daily" || v === "regular" || v === "regularly" || v === "current smoker" || v === "current" || v === "smoker" || v === "active smoker") {
    return { label: "Active smoker", tone: "danger" };
  }
  if (v === "occasionally" || v === "occasional" || v === "ex-smoker" || v === "ex smoker" || v === "former smoker" || v === "ancien fumeur") {
    return { label: "Ex-smoker", tone: "warning" };
  }
  if (v === "never" || v === "non-smoker" || v === "non smoker" || v === "jamais" || v === "non fumeur") {
    return { label: "Non-smoker", tone: "success" };
  }
  if (v.includes("daily") || v.includes("regular") || v.includes("current") || v.includes("actif") || v.includes("active")) {
    return { label: "Active smoker", tone: "danger" };
  }
  if (v.includes("occasion") || v.includes("ex") || v.includes("ancien") || v.includes("former")) {
    return { label: "Ex-smoker", tone: "warning" };
  }
  if (v.includes("never") || v.includes("non") || v.includes("jamais")) {
    return { label: "Non-smoker", tone: "success" };
  }

  return { label: "Unknown", tone: "neutral" };
};

const alcoholBackendToUi = (value) => {
  if (!value) return { label: "Unknown", tone: "neutral" };
  const v = String(value).toLowerCase().trim();

  if (v === "never" || v === "jamais" || v === "non-drinker") {
    return { label: "Non-drinker", tone: "success" };
  }
  if (v === "occasionally" || v === "occasional" || v === "occasionnel") {
    return { label: "Occasional drinker", tone: "warning" };
  }
  if (v === "daily" || v === "regular" || v === "regularly" || v === "regulier" || v === "frequent" || v === "quotidien") {
    return { label: "Regular drinker", tone: "danger" };
  }
  if (v.includes("never") || v.includes("jamais") || v.includes("non")) {
    return { label: "Non-drinker", tone: "success" };
  }
  if (v.includes("occasion")) {
    return { label: "Occasional drinker", tone: "warning" };
  }
  if (v.includes("daily") || v.includes("regular") || v.includes("frequent")) {
    return { label: "Regular drinker", tone: "danger" };
  }

  return { label: "Unknown", tone: "neutral" };
};

const sportBackendToUi = (value) => {
  if (!value) return { label: "Unknown", tone: "neutral" };
  const v = String(value).toLowerCase().trim();

  if (v === "never" || v === "none" || v === "jamais" || v === "aucun") {
    return { label: "Never", tone: "danger" };
  }
  if (v === "occasionally" || v === "occasional" || v === "occasionnel") {
    return { label: "Occasionally", tone: "warning" };
  }
  if (v === "weekly" || v === "hebdomadaire") {
    return { label: "Weekly", tone: "success" };
  }
  if (v === "daily" || v === "quotidien") {
    return { label: "Daily", tone: "success" };
  }
  if (v.includes("never") || v.includes("none") || v.includes("jamais")) {
    return { label: "Never", tone: "danger" };
  }
  if (v.includes("occasion")) {
    return { label: "Occasionally", tone: "warning" };
  }
  if (v.includes("week") || v.includes("hebdo")) {
    return { label: "Weekly", tone: "success" };
  }
  if (v.includes("daily") || v.includes("quotidien") || v.includes("jour")) {
    return { label: "Daily", tone: "success" };
  }

  return { label: "Unknown", tone: "neutral" };
};

/* ==================== MAPPINGS UI → BACKEND ==================== */

const smokingUiToBackend = (label) => {
  if (!label) return "Never";
  const l = String(label).toLowerCase().trim();
  if (l.includes("active") || l.includes("current")) return "Daily";
  if (l.includes("ex")) return "Occasionally";
  if (l.includes("non") || l.includes("never")) return "Never";
  return "Never";
};

const alcoholUiToBackend = (label) => {
  if (!label) return "Never";
  const l = String(label).toLowerCase().trim();
  if (l.includes("non") || l.includes("never")) return "Never";
  if (l.includes("occasion")) return "Occasionally";
  if (l.includes("regular") || l.includes("daily")) return "Daily";
  return "Never";
};

const sportUiToBackend = (label) => {
  if (!label) return "Never";
  const l = String(label).toLowerCase().trim();
  if (l.includes("never")) return "Never";
  if (l.includes("occasion")) return "Occasionally";
  if (l.includes("week")) return "Weekly";
  if (l.includes("daily")) return "Daily";
  return "Never";
};

/* ==================== HOOK ==================== */
export default function useMedicalData(userId, options = {}) {
  const { enabled = true, latestVitals = {} } = options;
  const dispatch = useDispatch();

  // ========== MEDICAL INFO ==========
  const medicalState = useSelector((s) => s.medical);
  const medicalLoading = medicalState?.loading;
  const medicalError = medicalState?.error;
  const medicalHasFetched = userId ? !!medicalState?.fetchedUserIds?.[userId] : false;
  const medicalUserLoading = userId ? !!medicalState?.loadingMap?.[userId] : false;
  const medicalData = userId
    ? medicalState?.byId?.[userId] ?? (medicalHasFetched ? medicalState?.data : null)
    : null;

  // ========== PERSONAL HISTORY (Lifestyle factors: Smoking, Alcohol, Sport) ==========
  const personalHistoryState = useSelector((s) => s.personalHistory);
  const personalHistoryItems = personalHistoryState?.items || [];
  const personalHistoryLoading = personalHistoryState?.loading;
  const personalHistoryFetched = personalHistoryState?.hasFetched;

  // ========== VACCINATIONS ==========
  const vaccinationState = useSelector((s) => s.vaccinations);
  const vaccinationData = useSelector((s) => {
    return s.vaccinations?.data || s.vaccinations?.items || s.vaccinations?.vaccinations || [];
  });
  const vaccinationsFetched = useSelector(selectVaccinationHasFetched);
  const vaccinationsLoading = useSelector(selectVaccinationLoading);

  // ========== SURGICAL HISTORY (Allergies + Surgeries) ==========
  const surgicalHistoryState = useSelector((s) => s.surgicalHistory);
  const surgicalHistoryItems = surgicalHistoryState?.data || [];
  const surgicalHistoryLoading = surgicalHistoryState?.loading;
  const surgicalHistoryFetched = surgicalHistoryState?.hasFetched;

  // ========== BMI HISTORY ==========
  const bmiHistoryState = useSelector((s) => s.bmiHistory);
  const bmiHistoryArr = bmiHistoryState?.data ?? bmiHistoryState?.items ?? [];
  const bmiHistoryFetched = bmiHistoryState?.hasFetched;
  const bmiHistoryLoading = bmiHistoryState?.loading;

  // ========== LOCAL STATE ==========
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [tempMetrics, setTempMetrics] = useState({
    height: "",
    weight: "",
    bloodPressure: "",
    heartRate: "",
    respiratoryRate: "",
    glucose: "",
  });

  const [tempAllergies, setTempAllergies] = useState([]);
  const [tempVaccinations, setTempVaccinations] = useState([]);
  const [tempSurgeries, setTempSurgeries] = useState([]);
  const [tempAllergiesStatus, setTempAllergiesStatus] = useState(null);

  const [tempHeight, setTempHeight] = useState("");
  const [tempWeight, setTempWeight] = useState("");

  // ========== FETCH MEDICAL INFO ==========
  useEffect(() => {
    if (!enabled || !userId) return;
    if (medicalHasFetched || medicalUserLoading) return;
    if (fetchingMedical.has(userId)) return;

    fetchingMedical.add(userId);
    dispatch(fetchMedicalInfoByUserId(userId)).finally(() => {
      fetchingMedical.delete(userId);
    });
  }, [dispatch, userId, enabled, medicalHasFetched, medicalUserLoading]);

  // ========== FETCH PERSONAL HISTORY ==========
  useEffect(() => {
    if (!enabled || !userId) return;
    if (personalHistoryFetched && fetchingPersonalHistory.lastUserId === userId) return;
    if (fetchingPersonalHistory.ids.has(userId)) return;

    fetchingPersonalHistory.ids.add(userId);
    dispatch(fetchPersonalHistoryByUserId(userId)).finally(() => {
      fetchingPersonalHistory.lastUserId = userId;
      fetchingPersonalHistory.ids.delete(userId);
    });
  }, [dispatch, userId, enabled, personalHistoryFetched, personalHistoryLoading]);

  // ========== FETCH VACCINATIONS ==========
  useEffect(() => {
    if (!enabled || !userId) return;
    if (vaccinationsFetched && fetchingVaccinations.lastUserId === userId) return;
    if (fetchingVaccinations.ids.has(userId)) return;

    fetchingVaccinations.ids.add(userId);

    dispatch(fetchVaccinations(userId))
      .unwrap()
      .then(() => {})
      .catch((err) => {
        console.error("❌ [useMedicalData] Error fetching vaccinations:", err);
      })
      .finally(() => {
        fetchingVaccinations.lastUserId = userId;
        fetchingVaccinations.ids.delete(userId);
      });
  }, [dispatch, userId, enabled, vaccinationsFetched, vaccinationsLoading]);

  // ========== FETCH SURGICAL HISTORY (Allergies + Surgeries) ==========
  useEffect(() => {
    if (!enabled || !userId) return;
    if (surgicalHistoryFetched && fetchingSurgicalHistory.lastUserId === userId) return;
    if (fetchingSurgicalHistory.ids.has(userId)) return;

    fetchingSurgicalHistory.ids.add(userId);
    dispatch(fetchSurgicalHistory(userId)).finally(() => {
      fetchingSurgicalHistory.lastUserId = userId;
      fetchingSurgicalHistory.ids.delete(userId);
    });
  }, [dispatch, userId, enabled, surgicalHistoryFetched, surgicalHistoryLoading]);

  // ========== FETCH BMI HISTORY ==========
  useEffect(() => {
    if (!enabled || !userId) return;
    if (bmiHistoryFetched && fetchingBmiHistory.lastUserId === userId) return;
    if (fetchingBmiHistory.ids.has(userId)) return;

    fetchingBmiHistory.ids.add(userId);
    dispatch(fetchBmiHistoryByUserId(userId)).finally(() => {
      fetchingBmiHistory.lastUserId = userId;
      fetchingBmiHistory.ids.delete(userId);
    });
  }, [dispatch, userId, enabled, bmiHistoryFetched, bmiHistoryLoading]);

  // ========== FETCH VITAL SIGNALS ==========
  useEffect(() => {
    if (!enabled || !userId) return;
    if (fetchingVitalSignals.lastUserId === userId) return;
    if (fetchingVitalSignals.ids.has(userId)) return;

    fetchingVitalSignals.ids.add(userId);
    dispatch(fetchVitalSignals({ userId })).finally(() => {
      fetchingVitalSignals.lastUserId = userId;
      fetchingVitalSignals.ids.delete(userId);
    });
  }, [dispatch, userId, enabled]);

  // ========== BMI CALCULATIONS ==========
  const latestBmi = useMemo(() => {
    if (!enabled || !Array.isArray(bmiHistoryArr) || bmiHistoryArr.length === 0) return null;
    return bmiHistoryArr[bmiHistoryArr.length - 1];
  }, [bmiHistoryArr, enabled]);

  const height = enabled ? latestBmi?.tailleCm ?? null : null;
  const weight = enabled ? latestBmi?.poidsKg ?? null : null;

  const { bmi: bmiFromHistory, status: bmiStatus } = useMemo(
    () => (!enabled ? { bmi: null, status: null } : calcBmi(height, weight)),
    [height, weight, enabled]
  );

  const chartData = useMemo(
    () => (!enabled ? [] : buildBmiChart(bmiHistoryArr)),
    [bmiHistoryArr, enabled]
  );

  // ========== CURRENT METRICS ==========
  const currentMetrics = useMemo(
    () => ({
      height: firstPresent(
        medicalData?.tailleCm, medicalData?.taille, medicalData?.heightCm, medicalData?.height,
        latestBmi?.tailleCm
      ),
      weight: firstPresent(
        medicalData?.poidsKg, medicalData?.poids, medicalData?.weightKg, medicalData?.weight,
        latestBmi?.poidsKg
      ),
      bloodPressure: firstPresent(
        medicalData?.tensionArterielle,
        medicalData?.bloodPressure,
        medicalData?.blood_pressure
      ),
      heartRate: firstPresent(
        medicalData?.frequenceCardiaque,
        medicalData?.heartRate,
        medicalData?.heart_rate
      ),
      respiratoryRate: firstPresent(
        medicalData?.frequenceRespiratoire,
        medicalData?.respiratoryRate,
        medicalData?.respiratory_rate
      ),
      glucose: firstPresent(
        medicalData?.niveauGlycemie,
        medicalData?.glycemie,
        medicalData?.glycemia,
        medicalData?.bloodGlucose,
        medicalData?.blood_glucose
      ),
    }),
    [medicalData, latestBmi]
  );

  const currentInsurances = useMemo(() => {
    if (Array.isArray(medicalData?.insurance)) return medicalData.insurance;
    if (Array.isArray(medicalData?.nomMutuelle)) return medicalData.nomMutuelle;
    if (typeof medicalData?.nomMutuelle === "string" && medicalData.nomMutuelle.trim()) {
      return [medicalData.nomMutuelle.trim()];
    }
    return [];
  }, [medicalData]);

  // ========== ALLERGIES (filtrées depuis surgical history type: "ALLERGY") ==========
  const currentAllergies = useMemo(() => {
    return surgicalHistoryItems
      .filter((item) => normalizeHistoryType(item.type) === "ALLERGY")
      .map((a) => ({
        id: a.id || a._id || a.backendId,
        label: a.item || a.label || a.allergen || "",
        allergen: a.item || a.label || a.allergen || "",
        severity: a.reaction || a.severity || "MILD",
        reaction: a.reaction || a.severity || "",
      }));
  }, [surgicalHistoryItems]);

  // ========== SURGERIES (filtrées depuis surgical history type: "SURGERY") ==========
  const currentSurgeries = useMemo(() => {
    return surgicalHistoryItems
      .filter((item) => normalizeHistoryType(item.type) === "SURGERY")
      .map((s) => ({
        backendId: s.id || s._id || s.backendId,
        id: s.id || s._id || s.backendId,
        label: s.item || s.label || "",
        year: s.date ? new Date(s.date).getFullYear() : undefined,
        notes: s.notes || "",
      }));
  }, [surgicalHistoryItems]);

  // ========== VACCINATIONS ==========
  const currentVaccinations = useMemo(() => {
    if (!Array.isArray(vaccinationData) || vaccinationData.length === 0) {
      return [];
    }

    const mapped = vaccinationData.map((v) => {
      let year = null;

      if (v.date) {
        const parsedDate = new Date(v.date);
        if (!isNaN(parsedDate.getTime())) {
          year = parsedDate.getFullYear();
        }
      }

      return {
        backendId: v.id || v._id,
        id: v.id || v._id,
        label: v.vaccine || v.label || v.name || "",
        vaccine: v.vaccine || v.label || v.name || "",
        year: year,
        date: v.date,
      };
    });

    return mapped;
  }, [vaccinationData]);

  // ========== LIFESTYLE FACTORS (Smoking, Alcohol, Sport) ==========
  const currentAllergiesStatus = useMemo(() => {
    const byLatest = (a, b) =>
      +new Date(b.createdAt || b.updatedAt || 0) - +new Date(a.createdAt || a.updatedAt || 0);

    const smoking = [...personalHistoryItems]
      .filter((it) => it.type === "SMOKING")
      .sort(byLatest)[0];

    const alcohol = [...personalHistoryItems]
      .filter((it) => it.type === "ALCOHOL")
      .sort(byLatest)[0];

    const sport = [...personalHistoryItems]
      .filter((it) => it.type === "SPORT" || it.type === "PHYSICAL_ACTIVITY")
      .sort(byLatest)[0];

    return {
      smoking: smokingBackendToUi(smoking?.value),
      alcohol: alcoholBackendToUi(alcohol?.value),
      sport: sportBackendToUi(sport?.value),
    };
  }, [personalHistoryItems]);

  // ========== BMI FROM MEDICAL ==========
  const bmiFromMedical = useMemo(() => {
    if (!enabled) return null;
    const h = isEditing ? tempMetrics.height : currentMetrics.height;
    const w = isEditing ? tempMetrics.weight : currentMetrics.weight;
    return calculateBMI(w, h);
  }, [enabled, isEditing, tempMetrics, currentMetrics]);

  // ========== EDIT MODE ==========
  const handleEditClick = useCallback(() => {
    if (!enabled) return;

    setTempMetrics({
      height: currentMetrics.height || "",
      weight: currentMetrics.weight || "",
      bloodPressure: currentMetrics.bloodPressure || String(latestVitals.bloodPressure ?? ""),
      heartRate: currentMetrics.heartRate || String(latestVitals.heartRate ?? ""),
      respiratoryRate: currentMetrics.respiratoryRate || String(latestVitals.respiratoryRate ?? ""),
      glucose: currentMetrics.glucose || String(latestVitals.glucose ?? ""),
    });

    setTempAllergies(currentAllergies);
    setTempVaccinations(currentVaccinations);
    setTempSurgeries(currentSurgeries);
    setTempAllergiesStatus(currentAllergiesStatus);
    setIsEditing(true);
  }, [
    enabled,
    currentMetrics,
    latestVitals,
    currentAllergies,
    currentVaccinations,
    currentSurgeries,
    currentAllergiesStatus,
  ]);

  const handleCancelClick = useCallback(() => {
    setIsEditing(false);
    setTempMetrics({
      height: "",
      weight: "",
      bloodPressure: "",
      heartRate: "",
      respiratoryRate: "",
      glucose: "",
    });
    setTempAllergies([]);
    setTempVaccinations([]);
    setTempSurgeries([]);
    setTempAllergiesStatus(null);
  }, []);

  // ========== SAVE CHANGES ==========
  const handleSaveClick = useCallback(
    async (overrides = {}) => {
      if (!enabled || !userId) {
        message.error("User ID is missing");
        throw new Error("User ID is missing");
      }

      setSaving(true);

      try {
        const heightNum = tempMetrics.height
          ? Number(normalizeNumber(tempMetrics.height))
          : null;

        const weightNum = tempMetrics.weight
          ? Number(normalizeNumber(tempMetrics.weight))
          : null;

        const heartRateNum = tempMetrics.heartRate
          ? Number(normalizeNumber(tempMetrics.heartRate))
          : null;

        const respRateNum = tempMetrics.respiratoryRate
          ? Number(normalizeNumber(tempMetrics.respiratoryRate))
          : null;

        const glucoseNum = tempMetrics.glucose
          ? Number(normalizeNumber(tempMetrics.glucose))
          : null;

        const medicalPayload = {
          groupeSanguin:
            overrides.groupeSanguin ?? medicalData?.groupeSanguin ?? undefined,

          maladiesChroniques:
            overrides.maladiesChroniques ?? medicalData?.maladiesChroniques ?? undefined,
        };

        const onlyMetrics = overrides?.onlyMetrics === true;

        /* ✅ CORRIGÉ : sectionErrors contient maintenant { name, detail }
           au lieu de juste le nom. C'est ce qui permettait à "vitals" de
           réussir en 201 tout en affichant "Failed to save changes" sans
           aucune indication de la VRAIE section fautive. */
        const sectionErrors = [];
        const runSection = async (name, fn) => {
          try {
            await fn();
          } catch (sectionErr) {
            const detail = extractErrorDetail(sectionErr);
            console.error(`❌ Error saving [${name}]:`, detail, sectionErr);
            sectionErrors.push({ name, detail });
          }
        };

        const hasExistingMedicalRecord =
          !!medicalData &&
          (Boolean(medicalData.id) ||
            Boolean(medicalData._id) ||
            Object.keys(medicalData || {}).length > 0);

        const upsertMedical = async () => {
          if (hasExistingMedicalRecord) {
            try {
              await dispatch(
                updateMedicalInfo({
                  userId,
                  values: medicalPayload,
                })
              ).unwrap();
              return;
            } catch (_updateErr) {
              await dispatch(
                createMedicalInfo({
                  userId,
                  values: medicalPayload,
                })
              ).unwrap();
              return;
            }
          }

          await dispatch(
            createMedicalInfo({
              userId,
              values: medicalPayload,
            })
          ).unwrap();
        };

        /* ✅ CORRIGÉ : on ne tente PUT/POST medical-information que si
           groupeSanguin ou maladiesChroniques ont réellement changé.
           Sur un sous-profil, cet appel se prend systématiquement un 400
           "Impossible de vérifier le patient" côté backend — même quand ces
           deux champs ne sont jamais modifiés depuis cet écran. Comme la
           section tournait à CHAQUE save, elle polluait sectionErrors et
           déclenchait "Failed to save changes" alors que tout le reste
           (vitals, allergies, etc.) avait réussi. */
        const medicalHasChanges =
          (medicalPayload.groupeSanguin !== undefined &&
            medicalPayload.groupeSanguin !== (medicalData?.groupeSanguin ?? undefined)) ||
          (medicalPayload.maladiesChroniques !== undefined &&
            medicalPayload.maladiesChroniques !== (medicalData?.maladiesChroniques ?? undefined));

        if (medicalHasChanges) {
          await runSection("medical information", upsertMedical);
        }

        if (heightNum && weightNum) {
          await runSection("height/weight", async () => {
            await dispatch(
              addBmiEntry({
                userId,
                values: { tailleCm: heightNum, poidsKg: weightNum },
              })
            ).unwrap();
          });
        }

        if (!onlyMetrics) {
          // ---------- ALLERGIES (type: "ALLERGY") ----------
          await runSection("allergies", async () => {
            const existingAllergyIds = new Set(
              currentAllergies
                .map((a) => pickPersistedId(a.id, a.backendId))
                .filter(Boolean)
            );
            const seenAllergyIds = new Set();

            for (const allergy of tempAllergies) {
              const item = String(allergy.label ?? allergy.allergen ?? "").trim();
              const persistedId = pickPersistedId(allergy.id, allergy.backendId);
              const values = {
                type: "ALLERGY",
                item,
                reaction: allergy.severity || allergy.reaction || "MILD",
              };

              if (persistedId) {
                if (!item) continue;
                seenAllergyIds.add(persistedId);
                await dispatch(
                  updateSurgicalHistory({ userId, id: persistedId, values })
                ).unwrap();
              } else if (item) {
                await dispatch(addSurgicalHistory({ userId, values })).unwrap();
              }
            }

            for (const id of existingAllergyIds) {
              if (!seenAllergyIds.has(id)) {
                await dispatch(removeSurgicalHistory({ userId, id })).unwrap();
              }
            }
          });

          // ---------- CHIRURGIES (type: "SURGERY") ----------
          await runSection("surgical history", async () => {
            const existingSurgeryIds = new Set(
              currentSurgeries
                .map((s) => pickPersistedId(s.backendId, s.id))
                .filter(Boolean)
            );
            const seenSurgeryIds = new Set();

            for (const surgery of tempSurgeries) {
              const item = String(surgery.label ?? surgery.item ?? "").trim();
              const persistedId = pickPersistedId(surgery.backendId, surgery.id);
              const values = {
                type: "SURGERY",
                item,
                date: surgery.year ? `${surgery.year}-01-01` : null,
                notes: surgery.notes || "",
              };

              if (persistedId) {
                if (!item) continue;
                seenSurgeryIds.add(persistedId);
                await dispatch(
                  updateSurgicalHistory({ userId, id: persistedId, values })
                ).unwrap();
              } else if (item) {
                await dispatch(addSurgicalHistory({ userId, values })).unwrap();
              }
            }

            for (const id of existingSurgeryIds) {
              if (!seenSurgeryIds.has(id)) {
                await dispatch(removeSurgicalHistory({ userId, id })).unwrap();
              }
            }
          });

          // ---------- VACCINATIONS ----------
          await runSection("vaccinations", async () => {
            const existingVaccinationIds = new Set(
              currentVaccinations
                .map((v) => pickPersistedId(v.backendId, v.id))
                .filter(Boolean)
            );
            const seenVaccinationIds = new Set();

            for (const vaccination of tempVaccinations) {
              const vaccine = String(
                vaccination.label ?? vaccination.vaccine ?? ""
              ).trim();
              const persistedId = pickPersistedId(vaccination.backendId, vaccination.id);
              const values = {
                vaccine,
                year: vaccination.year || null,
                date:
                  vaccination.date ||
                  (vaccination.year ? `${vaccination.year}-01-01` : null),
              };

              if (persistedId) {
                if (!vaccine) continue;
                seenVaccinationIds.add(persistedId);
                await dispatch(
                  updateVaccination({ userId, id: persistedId, values })
                ).unwrap();
              } else if (vaccine) {
                await dispatch(addVaccination({ userId, values })).unwrap();
              }
            }

            for (const id of existingVaccinationIds) {
              if (!seenVaccinationIds.has(id)) {
                await dispatch(removeVaccination({ userId, id })).unwrap();
              }
            }
          });

          // ---------- MODE DE VIE (tabac / alcool / sport) ----------
          await runSection("lifestyle factors", async () => {
            if (
              tempAllergiesStatus?.smoking &&
              tempAllergiesStatus.smoking.label !== currentAllergiesStatus?.smoking?.label
            ) {
              await dispatch(
                addPersonalHistory({
                  userId,
                  values: {
                    type: "SMOKING",
                    value: smokingUiToBackend(tempAllergiesStatus.smoking.label),
                  },
                })
              ).unwrap();
            }

            if (
              tempAllergiesStatus?.alcohol &&
              tempAllergiesStatus.alcohol.label !== currentAllergiesStatus?.alcohol?.label
            ) {
              await dispatch(
                addPersonalHistory({
                  userId,
                  values: {
                    type: "ALCOHOL",
                    value: alcoholUiToBackend(tempAllergiesStatus.alcohol.label),
                  },
                })
              ).unwrap();
            }

            if (
              tempAllergiesStatus?.sport &&
              tempAllergiesStatus.sport.label !== currentAllergiesStatus?.sport?.label
            ) {
              await dispatch(
                addPersonalHistory({
                  userId,
                  values: {
                    type: "SPORT",
                    value: sportUiToBackend(tempAllergiesStatus.sport.label),
                  },
                })
              ).unwrap();
            }
          });
        }

        // ========== VITALS SIGNALS ==========
        const now = new Date().toISOString();
        const bp = parseBP(tempMetrics.bloodPressure || "");
        const vitalsPayloads = [];

        if (glucoseNum != null && !Number.isNaN(glucoseNum)) {
          vitalsPayloads.push({
            type: "GLYCEMIA",
            value: glucoseNum,
            unit: "mg/dL",
            measuredAt: now,
          });
        }

        if (heartRateNum != null && !Number.isNaN(heartRateNum)) {
          vitalsPayloads.push({
            type: "HEART_RATE",
            value: heartRateNum,
            unit: "bpm",
            measuredAt: now,
          });
        }

        if (respRateNum != null && !Number.isNaN(respRateNum)) {
          vitalsPayloads.push({
            type: "RESP_RATE",
            value: respRateNum,
            unit: "rpm",
            measuredAt: now,
          });
        }

        if (bp.sys != null) {
          vitalsPayloads.push({
            type: "BP_SYS",
            value: bp.sys,
            unit: "mmHg",
            measuredAt: now,
          });
        }

        if (bp.dia != null) {
          vitalsPayloads.push({
            type: "BP_DIA",
            value: bp.dia,
            unit: "mmHg",
            measuredAt: now,
          });
        }

        await runSection("vital signs", async () => {
          for (const v of vitalsPayloads) {
            await dispatch(
              addVitalSignal({
                userId,
                values: v,
              })
            ).unwrap();
          }
        });

        if (onlyMetrics) {
          await Promise.allSettled([
            dispatch(fetchMedicalInfoByUserId(userId)).unwrap(),
            dispatch(fetchVitalSignals({ userId })).unwrap(),
            dispatch(fetchBmiHistoryByUserId(userId)).unwrap(),
          ]);

          if (sectionErrors.length) {
            const detail = sectionErrors
              .map((e) => `${e.name}: ${e.detail}`)
              .join(" | ");
            throw new Error(`Could not save: ${detail}`);
          }

          setIsEditing(false);
          message.success("Metrics updated successfully!");
          return true;
        }

        // ========== REFRESH ALL DATA ==========
        await Promise.allSettled([
          dispatch(fetchSurgicalHistory(userId)).unwrap(),
          dispatch(fetchVaccinations(userId)).unwrap(),
          dispatch(fetchPersonalHistoryByUserId(userId)).unwrap(),
          dispatch(fetchMedicalInfoByUserId(userId)).unwrap(),
          dispatch(fetchVitalSignals({ userId })).unwrap(),
          dispatch(fetchBmiHistoryByUserId(userId)).unwrap(),
        ]);

        /* ✅ CORRIGÉ : le message d'erreur final contient maintenant le NOM
           ET LA RAISON de chaque section en échec (ex: "allergies: item
           should not be empty"), au lieu de juste "Could not save: allergies".
           Ce message remonte tel quel jusqu'au toast antd ET jusqu'au
           catch de onSaveChanges dans Overviews.jsx — regarde la console
           et le toast en haut de l'écran après un Save pour voir la vraie
           cause, plutôt que le message générique "Failed to save changes". */
        if (sectionErrors.length) {
          const detail = sectionErrors
            .map((e) => `${e.name}: ${e.detail}`)
            .join(" | ");
          throw new Error(`Could not save: ${detail}`);
        }

        setIsEditing(false);
        message.success("Medical information updated successfully!");
        return true;
      } catch (err) {
        message.error(err?.message || "Error saving medical information");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [
      enabled,
      userId,
      tempMetrics,
      tempAllergies,
      tempVaccinations,
      tempSurgeries,
      tempAllergiesStatus,
      currentAllergiesStatus,
      currentAllergies,
      currentSurgeries,
      currentVaccinations,
      medicalData,
      dispatch,
    ]
  );

  const setMetric = useCallback((field, value) => {
    setTempMetrics((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ========== BMI HANDLERS ==========
  const handleBmiEditClick = useCallback(() => {
    if (!enabled) return;
    setTempHeight(height ?? "");
    setTempWeight(weight ?? "");
  }, [height, weight, enabled]);

  const handleBmiCancelClick = useCallback(() => {
    setTempHeight("");
    setTempWeight("");
  }, []);

  const handleBmiSaveClick = useCallback(
    async (newHeight, newWeight) => {
      if (!enabled || !userId) return;

      const heightToSave =
        newHeight !== undefined ? normalizeNumber(newHeight) : normalizeNumber(tempHeight);
      const weightToSave =
        newWeight !== undefined ? normalizeNumber(newWeight) : normalizeNumber(tempWeight);

      if (!heightToSave || !weightToSave) {
        message.warning("Please enter a valid height and weight.");
        return;
      }

      try {
        setSaving(true);

        await dispatch(
          addBmiEntry({
            userId,
            values: { tailleCm: Number(heightToSave), poidsKg: Number(weightToSave) },
          })
        ).unwrap();

        await dispatch(fetchBmiHistoryByUserId(userId));
        await dispatch(fetchMedicalInfoByUserId(userId));

        setTempHeight("");
        setTempWeight("");
        message.success("✅ BMI updated!");
      } catch (err) {
        console.error("BMI update error:", err);
        message.error("❌ Error updating BMI.");
      } finally {
        setSaving(false);
      }
    },
    [dispatch, userId, tempHeight, tempWeight, enabled]
  );

  const isLoading = enabled && !medicalHasFetched && (medicalLoading || medicalUserLoading);

  return {
    loading: isLoading,
    error: enabled ? medicalError : null,
    saving: enabled ? saving : false,

    metrics: currentMetrics,
    allergies: currentAllergies,
    vaccinations: currentVaccinations,
    surgeries: currentSurgeries,
    allergiesStatus: currentAllergiesStatus,
    bloodType: medicalData?.groupeSanguin ?? "",
    diseases: medicalData?.maladiesChroniques ?? "",
    insurances: currentInsurances,
    bmi: bmiFromMedical,

    height,
    weight,
    bmiFromHistory,
    bmiStatus,
    chartData,

    isEditing: enabled ? isEditing : false,

    tempMetrics,
    tempAllergies,
    tempVaccinations,
    tempSurgeries,
    tempAllergiesStatus,
    tempHeight,
    tempWeight,

    setMetric,
    setTempAllergies,
    setTempVaccinations,
    setTempSurgeries,
    setTempAllergiesStatus,
    setTempHeight,
    setTempWeight,

    handleEditClick,
    handleCancelClick,
    handleSaveClick,
    handleBmiEditClick,
    handleBmiCancelClick,
    handleBmiSaveClick,
  };
}